import { MdCheck, MdClose, MdModeEditOutline } from "react-icons/md";
import { RoleValidator } from "../../../Components/Common";
import {
  MESSAGES,
  TASK_STATUS_LABELS,
  getStatusIconAndColor,
} from "../Constants";
import { ROLES, ROLES_LABELS } from "../../../Constants/Strings";

const TaskDetailsInfo = ({
  task,
  editMode,
  setEditMode,
  handleSave,
  handleChange,
  siteUser,
  userLoading,
  formData,
}) => {
  if (!task) return null;
  const isSubTask = task?.super_task !== null;

  return (
    <div className="h-full min-h-[45vh] px-2">
      <div className="flex justify-between items-center mb-2">
        <div className="flex flex-col justify-start gap-1">
          <h2 className="text-lg font-semibold text-primary">
            <span className="border-b-2 border-gray-300 pb-1 inline-block">
              {isSubTask ? "Sub-task" : "Task"} Details
            </span>
          </h2>
          <h3 className="text-base font-bold text-gray-800">
            {task?.title} 
          </h3>
        </div>

        <div className="flex gap-3 text-blue-500">
          {editMode ? (
            <>
              <button onClick={handleSave} disabled={userLoading}>
                <MdCheck size={22} className="text-green-500" />
              </button>
              <button onClick={() => setEditMode(false)}>
                <MdClose size={22} className="text-red-500" />
              </button>
            </>
          ) : (
            <RoleValidator
              allowedRoles={[
                ROLES.MANAGING_DIRECTOR,
                ROLES.HEAD_OFFICE,
                ROLES.GOVERNMENT_OFFICER,
                ROLES.PROJECT_MANAGER,
              ]}
              mode="disable"
            >
              <button onClick={() => setEditMode(true)}>
                <MdModeEditOutline size={20} />
              </button>
            </RoleValidator>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mt-2">
        <div className="md:col-span-3 md:row-span-2">
          <strong>Description</strong>
          <textarea
            className={`text-sm font-semibold w-[95%] mt-1 resize-none rounded-md border border-gray-300 p-2 transition-colors duration-200 ${
              editMode
                ? "bg-white cursor-text focus:ring-2 focus:ring-primary focus:outline-none"
                : "bg-gray-50 text-gray-600 cursor-not-allowed"
            }`}
            rows={4}
            value={formData?.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
            readOnly={!editMode}
            disabled={!editMode}
          />
        </div>

        <div className="md:col-span-2">
          <strong className="truncate-[90%]">
            Assigned To (
            {task?.super_task
              ? ROLES_LABELS[ROLES.SITE_ENGINEER]
              : ROLES_LABELS[ROLES.PROJECT_ENGINEER]}
            )
          </strong>
          {editMode ? (
            <select
              value={formData.assigned_to || ""}
              onChange={(e) => handleChange("assigned_to", e.target.value)}
              className="border-b-2 border-gray-300 outline-none rounded-md text-sm font-semibold mt-1 w-full"
            >
              <option value="">Select User</option>
              {siteUser.map((u) => (
                <option key={u.user.id} value={u.user.id}>
                  {u.user.first_name} {u.user.last_name}
                </option>
              ))}
            </select>
          ) : task?.assigned_to ? (
            <div className="flex items-center gap-2 mt-1">
              {task.assigned_to.profile_photo ? (
                <img
                  src={task.assigned_to.profile_photo}
                  alt="Profile"
                  className="w-7 h-7 rounded-full"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-white">
                  {task.assigned_to.first_name?.[0] || "?"}
                </div>
              )}
              <span>
                {task.assigned_to.first_name} {task.assigned_to.last_name}
              </span>
            </div>
          ) : (
            <p className="text-gray-400 italic mt-1">
              {MESSAGES.NOT_ASSIGNED_YET}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <strong>Reviewed By</strong>
          {task?.last_reviewed_by ? (
            <div className="flex items-center gap-2 mt-1">
              {task.last_reviewed_by.profile_photo ? (
                <img
                  src={task.last_reviewed_by.profile_photo}
                  alt="Profile"
                  className="w-7 h-7 rounded-full"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-white">
                  {task.last_reviewed_by.first_name?.[0] || "?"}
                </div>
              )}
              <span>
                {task.last_reviewed_by.first_name}{" "}
                {task.last_reviewed_by.last_name}
              </span>
            </div>
          ) : (
            <p className="text-gray-400 italic mt-1">
              {MESSAGES.NOT_ASSIGNED_YET}
            </p>
          )}
        </div>

        <div>
          <strong>Weight</strong>
          <div className="mt-1">
            {editMode ? (
              <input
                type="number"
                value={formData.weight || ""}
                onChange={(e) => handleChange("weight", e.target.value)}
                className="text-sm font-semibold border border-gray-300 rounded-md py-1 px-2 outline-none w-24 focus:ring-2 focus:ring-primary"
              />
            ) : task?.weight ? (
              <p className="text-gray-700">{task.weight}</p>
            ) : (
              <p className="text-gray-400 italic">{MESSAGES.NOT_SET}</p>
            )}
          </div>
        </div>

        <div>
          <strong>Start Date</strong>
          <div className="mt-1">
            {editMode ? (
              <input
                type="date"
                value={formData.start_date || ""}
                onChange={(e) => handleChange("start_date", e.target.value)}
                className="border border-gray-300 rounded-md outline-none text-sm font-semibold py-1 px-2 w-32 focus:ring-2 focus:ring-primary"
              />
            ) : (
              <p className="text-gray-700">
                {task?.start_date || MESSAGES.NOT_SET}
              </p>
            )}
          </div>
        </div>

        <div>
          <strong>Due Date</strong>
          <div className="mt-1">
            {editMode ? (
              <input
                type="date"
                value={formData.estimated_end_date || ""}
                onChange={(e) =>
                  handleChange("estimated_end_date", e.target.value)
                }
                className="border border-gray-300 rounded-md outline-none text-sm font-semibold py-1 px-2 w-32 focus:ring-2 focus:ring-primary"
              />
            ) : (
              <p className="text-gray-700">
                {task?.estimated_end_date || MESSAGES.NOT_SET}
              </p>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <strong>Status</strong>
          <div className="mt-1">
            {task?.status ? (
              (() => {
                const { icon, color } = getStatusIconAndColor(task.status);
                return (
                  <span
                    className={`inline-flex items-center gap-2 pl-2 pr-3 py-1 rounded-full text-xs font-semibold ${color}`}
                  >
                    {icon}
                    {TASK_STATUS_LABELS[task.status] || task.status}
                  </span>
                );
              })()
            ) : (
              <span className="text-gray-400 italic">{MESSAGES.NOT_SET}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsInfo;
